import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import moment from "moment";
import { isEmpty } from "lodash";
import Select from "react-select";

import CustomDatePicker from "../../components/CustomDatePicker";
import MyAccountFilter from "../MyAccount/MyAccountFilter";
import { apiGet } from "../../Utils/apiFetch";
import { apiPath } from "../../Utils/apiPath";
import { levelMap, toFixedWithoutRounding } from "../../Utils/Constant";
import AuthContext from "../../context/AuthContext";
import { useContextSelector } from "use-context-selector";

const userOptions = [
  { value: "1", label: "User 1" },
  { value: "2", label: "User 2" },
  { value: "3", label: "User 3" },
];

const HIERARCHY_KEY = "casinoDownlineHierarchy";

const readHierarchy = () => {
  try {
    return JSON.parse(localStorage.getItem(HIERARCHY_KEY)) || [];
  } catch {
    return [];
  }
};

const writeHierarchy = (list) => {
  localStorage.setItem(HIERARCHY_KEY, JSON.stringify(list));
};

const ReportByCasinoDownline = () => {
  const params = useParams();
  const user = useContextSelector(AuthContext, (ctx) => ctx.user);
  const tableRef = useRef();

  const [hierarchy, setHierarchy] = useState(readHierarchy());

  const previousDate = moment().subtract(1, "day").format("YYYY-MM-DD HH:mm:ss");
  const currentDate = moment().format("YYYY-MM-DD HH:mm:ss");

  const DEFAULT_FILTER = {
    page: 1,
    limit: 10,
    fromPeriod: previousDate,
    toPeriod: currentDate,
    filterByDay: "",
    created_by: params?.id || user?._id,
    userType: params?.user_type || user?.userType,
  };

  const [filter, setFilter] = useState(DEFAULT_FILTER);
  const [data, setData] = useState([]);
  const [sumData, setSumData] = useState({});

  const getData = async (activeFilter) => {
    const { status, data: response_users } = await apiGet(apiPath.casinoAllMarket, activeFilter);
    if (status === 200 && response_users.success) {
      setData(
        response_users.results?.data?.filter((res) => {
          return Math.abs(res?.stake?.amount) > 0;
        }) || [],
      );
      setSumData(response_users.results?.sumData || {});
    }
  };

  useEffect(() => {
    getData(filter);
  }, [filter.page, filter.limit, filter.created_by, filter.userType]);

  useEffect(() => {
    const createdBy = params?.id || user?._id;
    const userType = params?.user_type || user?.userType;
    setFilter((prev) =>
      prev.created_by === createdBy && prev.userType === userType
        ? prev
        : { ...prev, page: 1, created_by: createdBy, userType },
    );

    // keep the stored trail in sync with the current route: if we're back
    // at the root, clear it; if the route matches a level already in the
    // trail (e.g. browser back button), trim to that level instead of
    // trusting whatever was appended after it
    if (!params?.id) {
      writeHierarchy([]);
      setHierarchy([]);
    } else {
      const stored = readHierarchy();
      const idx = stored.findIndex((h) => h._id === params.id);
      const next = idx >= 0 ? stored.slice(0, idx + 1) : stored;
      writeHierarchy(next);
      setHierarchy(next);
    }
  }, [params?.id, params?.user_type]);

  // called when the user clicks into a downline row — appends that user to
  // the stored trail before the route change unmounts this component
  const drillInto = (item) => {
    const stored = readHierarchy();
    const next = [...stored, { _id: item?._id, username: item?.username, userType: item?.userType }];
    writeHierarchy(next);
    setHierarchy(next);
  };

  // clicking an earlier breadcrumb should truncate the trail back to that point
  const jumpToLevel = (index) => {
    const trimmed = hierarchy.slice(0, index + 1);
    writeHierarchy(trimmed);
    setHierarchy(trimmed);
  };


  const applyPreset = (preset) => {
    const nextFilter = { ...filter, page: 1, filterByDay: preset };
    if (preset === "today") {
      nextFilter.fromPeriod = moment().startOf("day").format("YYYY-MM-DD HH:mm:ss");
      nextFilter.toPeriod = moment().endOf("day").format("YYYY-MM-DD HH:mm:ss");
    } else if (preset === "yesterday") {
      nextFilter.fromPeriod = moment().subtract(1, "day").startOf("day").format("YYYY-MM-DD HH:mm:ss");
      nextFilter.toPeriod = moment().subtract(1, "day").endOf("day").format("YYYY-MM-DD HH:mm:ss");
    }
    setFilter(nextFilter);
    if (filter.page === nextFilter.page) getData(nextFilter);
  };

  const handleLimitChange = (e) => setFilter((prev) => ({ ...prev, limit: Number(e.target.value), page: 1 }));

  const plSpan = (value) =>
    Math.sign(value) === -1 ? (
      <span className="loss-text">({toFixedWithoutRounding(Math.abs(value))})</span>
    ) : (
      <span>{toFixedWithoutRounding(value)}</span>
    );

  const totalRecords = data?.length || 0;

  return (
    <app-pnl-casino-downline className="ng-star-inserted">
      <div className="main_wrap">
        <div className="box">
          <h2>Casino Profit/Loss Report by Downline</h2>
        </div>

        <ul className="report-tab-wrap ng-star-inserted">
          <li id="reportType_exchange" data-reporttabtype={0} className="report-tab select">
            <a>Casino</a>
          </li>
        </ul>

        <div className="function-wrap">
          <ul className="input-list flowroot">
            <li />
            <li>
              <label>Period</label>
            </li>
            <CustomDatePicker filter={filter} setFilter={setFilter} />
            <li>
              <label>User List:</label>
              <Select
                options={userOptions}
                placeholder="Select User"
                isSearchable={false}
                isClearable={true}
                className="ng-select ng-select-single ng-select-searchable ng-select-clearable"
                classNamePrefix="p-dropdown"
              />
            </li>
          </ul>
          <ul className="input-list flowroot">
            <li>
              <a
                id="today"
                className="btn"
                onClick={(e) => {
                  e.preventDefault();
                  applyPreset("today");
                }}
              >
                Just for today
              </a>
            </li>
            <li>
              <a
                id="yesterday"
                className="btn"
                onClick={(e) => {
                  e.preventDefault();
                  applyPreset("yesterday");
                }}
              >
                From yesterday
              </a>
            </li>
            <li>
              <a
                id="getPL"
                className="btn-send"
                onClick={(e) => {
                  e.preventDefault();
                  getData(filter);
                }}
              >
                Get P &amp; L
              </a>
            </li>
          </ul>
        </div>

        <div id="noReportMessage" />

        <div id="userTreeDiv" className="total_all ng-star-inserted">
          <span>
            <div id="treePath" className="agent_path">
              <ul id="userTree" className="agent_path-L">
                <li>
                  <Link to="/reports/pnl-casino-downline" onClick={() => jumpToLevel(-1)}>
                    <span className={`Bdlevel ${levelMap[user?.userType] || ""} p premium`} /> {user?.username}{" "}
                  </Link>
                </li>
                {hierarchy.map((h, i) => (
                  <li key={h._id}>
                    <Link
                      to={`/reports/pnl-casino-downline/${h._id}/${h.userType}`}
                      onClick={() => jumpToLevel(i)}
                    >
                      <span className={`Bdlevel ${levelMap[h.userType] || ""} p premium`} /> {h.username}{" "}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </span>
          <span className="spacer" />
          <span>
            <label>Last:</label>
            <select name="limit" id="limit" className="ng-star-inserted" value={filter.limit} onChange={handleLimitChange}>
          <option value={10}>10 Txn</option>{" "}
              <option value={25}>25 Txn</option>{" "}
              <option value={50}>50 Txn</option>
              <option value={100}>100 Txn</option>
            </select>
          </span>
          <span className="total-users" style={{ fontWeight: "600" }}>
            Total Records: {totalRecords}{" "}
          </span>
          <MyAccountFilter type={"new"} tableRef={tableRef} fileName="report-casino-downline" />
        </div>

        <div id="reportDiv" className="over-wrap" style={{ maxHeight: "calc(100% - 32px - 93px - 55px)" }}>
          <div className="table-responsive data-table">
            <table ref={tableRef} id="table_DL" className="table01 table-pt">
              <tbody>
                <tr id="table_DL">
                  <th id="header_userName" width="16%" className="align-L sort_asc">
                    UID
                  </th>
                  <th width="16%" className="sort_asc">
                    Stake
                  </th>
                  <th width="16%" className="sort_asc">
                    {" "}
                    Casino P/L{" "}
                  </th>
                  <th id="header_tax_agent_2" width="16%" className="sort_asc">
                    Comm.
                  </th>
                  <th width="16%" className="sort_asc">
                    {" "}
                    Total P/L{" "}
                  </th>
                </tr>
              </tbody>

              <tbody id="content">
                {!isEmpty(data) ? (
                  data.map((item, index) => {
                    const stakeAmount = Number(item?.stake?.stakeAmount || 0);
                    const casinoPL = Number(item?.stake?.amount || 0);
                    const commission = Number(item?.stake?.commission || 0);
                    const totalPL = Number(item?.stake?.finalAmount || 0);

                    return (
                      <tr id="table_DL" key={item?._id || index}>
                        <td className="align-L">
                          <a id="_bySport" className="expand-open" />
                          {item?.userType === "user" ? (
                            <Link to={`/my-account/account-summary/${item?._id}/${item?.userType}`} className="ico_account">
                              <span className={`Bdlevel ${levelMap[item?.userType]}`} />
                              {item?.username}{" "}
                            </Link>
                          ) : (
                            <Link
                            style={{textDecoration:"underline"}}
                              id="_userName"
                              className="ico_account"
                              to={`/reports/pnl-casino-downline/${item?._id}/${item?.userType}`}
                              onClick={() => drillInto(item)}
                            >
                              <span className={`Bdlevel ${levelMap[item?.userType]}`} />
                              {item?.username}{" "}
                            </Link>
                          )}
                        </td>
                        <td>
                          <span>{toFixedWithoutRounding(stakeAmount)}</span>
                        </td>
                        <td id="_profitLoss" className={casinoPL == 0 ? "" : casinoPL < 0 ? "red" : "green"}>
                          <div>{plSpan(casinoPL)}</div>
                        </td>
                        <td id="_tax2">
                          <div>
                            <span>{toFixedWithoutRounding(commission)}</span>
                          </div>
                        </td>
                        <td id="_profitLossUpLine" className={totalPL == 0 ? "" : totalPL < 0 ? "red" : "green"}>
                          <div>{plSpan(totalPL)}</div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={5}>You have no bets in this time period.</td>
                  </tr>
                )}

                {!isEmpty(data) && (
                  <tr className="total">
                    <td className="align-L">Total</td>
                    <td>
                      <span> {toFixedWithoutRounding(sumData?.stakeAmount || 0)} </span>
                    </td>
                    <td className={sumData?.PlayerPL == 0 ? "" : sumData?.PlayerPL < 0 ? "red" : "green"}>
                      <div>{plSpan(sumData?.PlayerPL || 0)}</div>
                    </td>
                    <td id="_totalComm">
                      <span> {toFixedWithoutRounding(sumData?.commission || 0)} </span>
                    </td>
                    <td id="totalplayerPL" className={sumData?.finalPlayerPL == 0 ? "" : sumData?.finalPlayerPL < 0 ? "red" : "green"}>
                      <span> {toFixedWithoutRounding(sumData?.finalPlayerPL || 0)} </span>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </app-pnl-casino-downline>
  );
};

export default ReportByCasinoDownline;
